// GitHub user-token connect card data layer (arc 101 slice 05, #21). A member's
// own fine-grained PAT lets developerz.ai act on GitHub AS them (the user-token
// work lane) on the repos they authorize. Reads/writes `/v1/me/github-token` —
// same `write:self:keys` + user-session gating as `/v1/me/keys`. The token is
// write-only: no response ever echoes it back. Mirrors lib/my-keys.ts.

import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

export const GITHUB_TOKEN_QUERY_KEY = 'github_token';

/** The live connection as `GET /v1/me/github-token` reports it (`null` = none). */
export type GithubTokenConnection = {
  readonly connected: true;
  /** ISO expiry, or `null` when the PAT carries no expiry. */
  readonly expires_at: string | null;
  /** GitHub full names (`owner/name`) the token is authorized for. */
  readonly repos: readonly string[];
};

/** POST echo — the connected identity + scope. Never carries the token. */
export type GithubTokenResult = {
  readonly connected: true;
  readonly login: string;
  readonly expires_at: string;
  readonly repos: readonly string[];
};

/**
 * The acting user's GitHub-token connection. Idle with no session, like the
 * other `/v1/me/*` reads. `connection` is `null` when nothing is connected —
 * that is DATA (a 200), not an empty/error state, so the card renders the
 * connect form from it.
 */
export function useGithubTokenConnection(
  token: string | null = SESSION_TOKEN,
  /** False holds the read — the container's `sso_pinned` gate (#7426). */
  allowed: () => boolean = () => true,
) {
  return createQuery<{ connection: GithubTokenConnection | null }>(() => ({
    queryKey: [GITHUB_TOKEN_QUERY_KEY, token, activeAccountId()],
    enabled: token !== null && allowed(),
    queryFn: () =>
      apiFetch<{ connection: GithubTokenConnection | null }>('/v1/me/github-token', token),
  }));
}

/**
 * Connect a fine-grained PAT for the authorized repos. 4xx/502 throw `ApiError`
 * whose `detail` names the refusal (classic token, wrong account, unreachable);
 * the container surfaces it inline.
 */
export async function connectGithubToken(
  input: { readonly token: string; readonly repos: readonly string[] },
  token: string | null = SESSION_TOKEN,
): Promise<GithubTokenResult> {
  return apiFetch<GithubTokenResult>('/v1/me/github-token', token, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Disconnect the PAT (200 `{connected:false}`; 404 when none was connected). */
export async function disconnectGithubToken(
  token: string | null = SESSION_TOKEN,
): Promise<{ connected: false }> {
  return apiFetch<{ connected: false }>('/v1/me/github-token', token, { method: 'DELETE' });
}

/**
 * Parse the repos textarea into GitHub full names. One `owner/name` per line OR
 * comma-separated (or both); trims each entry, drops blanks, and dedupes while
 * preserving first-seen order.
 */
export function parseRepos(input: string): string[] {
  const seen = new Set<string>();
  for (const raw of input.split(/[\n,]/)) {
    const name = raw.trim();
    if (name.length > 0) seen.add(name);
  }
  return [...seen];
}
